import { UIEvent, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import {
  AdminsApi,
  AdminsEcommerceProductPricelistListResponseItem,
  AdminsEcommerceProductPricelistListSortEnum,
  AdminsEcommerceProductPriceListsUpdateV2Request,
  AdminsGetProductListResponse,
  AdminsSupplyChainInventoryStockUpdateRequest,
  Configuration,
  EcommerceApi,
  EcommerceProductStatusEnum,
  OrderDirectionEnum,
  SupplychainApi,
} from 'generated/api'
import Toast from 'components/toast/Toast'
import { axiosDefaultConfig } from 'constants/axios'
import { DropDownEcommerceProductStatusEnum } from 'constants/dropdown'
import FormProductPriceValue, { FormPrice } from 'constants/interfaceFormik/productStockPrice'
import debounce from 'helpers/debounce'
import dropdownScrollFetch from 'helpers/dropdownScrollFetch'

const axiosConfig = new Configuration(axiosDefaultConfig)
const adminsApi = new AdminsApi(axiosConfig)
const ecommerceApi = new EcommerceApi(axiosConfig)
const supplychainApi = new SupplychainApi(axiosConfig)

export const usePricelists = () => {
  const { toast, toastContainer } = Toast()
  const navigate = useNavigate()
  const params = useParams()
  const [searchParams] = useSearchParams()
  const location = useLocation()

  const [stockLocationId, setStockLocationId] = useState<string>('')
  const [isInitialFetchProductPrice, setInitialFetchProductPrice] = useState(true)
  const [isLoadingFetchProductPrice, setLoadingFetchProductPrice] = useState(false)
  const [isFailedFetchProductPrice, setFailedFetchProductPrice] = useState(false)
  const [productsPrice, setProductsPrice] = useState<
    AdminsEcommerceProductPricelistListResponseItem[]
  >([])
  const [limitPageProductsPrice, setLimitPageProductsPrice] = useState(10)
  const [currentPageProductsPrice, setCurrentPageProductsPrice] = useState(1)
  const [totalRowsProductsPrice, setTotalRowsProductsPrice] = useState(0)
  const [productPriceStatus, setProductPriceStatus] = useState<EcommerceProductStatusEnum>(
    EcommerceProductStatusEnum.Active,
  )
  const [sortProductsPrice, setSortProductsPrice] =
    useState<AdminsEcommerceProductPricelistListSortEnum>(
      AdminsEcommerceProductPricelistListSortEnum.ProductName,
    )
  const [directionProductsPrice, setDirectionProductsPrice] = useState<OrderDirectionEnum>(
    OrderDirectionEnum.Asc,
  )
  const [isInitFetchProducts, setInitFetchProducts] = useState(true)
  const [isLoadingProducts, setLoadingProducts] = useState(false)
  const [isFailedProducts, setFailedProducts] = useState(false)
  const [productStatus] = useState<DropDownEcommerceProductStatusEnum>(
    DropDownEcommerceProductStatusEnum.ACTIVE,
  )
  const [productNameSearch, setProductNameSearch] = useState('')
  const [products, setProducts] = useState<AdminsGetProductListResponse[]>([])
  const [limitPageProducts] = useState(10)
  const [pageProducts, setPageProducts] = useState(1)
  const [totalPagesProducts, setTotalPagesProducts] = useState(0)
  const [formStockPrice, setFormStockPrice] = useState<FormProductPriceValue>({
    productId: '',
    endDateUtc0: 0,
    isActive: true,
    isShowMember: true,
    isShowWorker: true,
    isShowAmbassador: true,
    isShowFarmer: true,
    /**
     * @deprecated
     * This property is deprecated.
     */
    minimumOrder: 1,
    /**
     * @deprecated
     * This property is deprecated.
     */
    price: 0,
    prices: [
      {
        id: Date.now(),
        minimumQuantity: 1,
        maximumQuantity: 0,
        price: 0,
      },
    ],
    preparationDuration: 0,
    startDateUtc0: dayjs().startOf('day').valueOf(),
    stockAmount: 0,
    storeId: '',
    isInquiryAllowed: false,
    productName: '',
  })

  const [isLoadingForm, setIsLoadingForm] = useState(false)
  const [stockId, setStockId] = useState<string>('')
  const [priceListId, setPriceListId] = useState('')

  useEffect(() => {
    if (!isInitialFetchProductPrice) {
      onFetchProductPrice(stockLocationId)
    }
  }, [
    productPriceStatus,
    currentPageProductsPrice,
    limitPageProductsPrice,
    sortProductsPrice,
    directionProductsPrice,
  ])

  useEffect(() => {
    if (!isInitFetchProducts) fetchProducts()
  }, [pageProducts, limitPageProducts, productStatus, productNameSearch])

  useEffect(() => {
    if (
      params.id &&
      location.pathname === `/inventory/stock-location/stock/${params.id}/pricelist/create`
    ) {
      setFormStockPrice({
        ...formStockPrice,
        storeId: params.id,
      })
    } else if (
      params.id &&
      location.pathname === `/inventory/stock-location/stock/${params.id}/pricelist/edit`
    ) {
      if (searchParams.get('priceListId') && searchParams.get('stockId')) {
        onFetchDetail()
      } else {
        navigate(`/inventory/stock-location/stock/${params.id}`)
      }
    }
  }, [location.pathname, params.id])

  const onChangePageProducts = (page: number) => {
    setPageProducts(page)
  }

  const onDropDownScrollFetch = (event: UIEvent<HTMLDivElement>) => {
    dropdownScrollFetch(event, pageProducts, totalPagesProducts, () =>
      onChangePageProducts(pageProducts + 1),
    )
  }

  const onChangeProductNameSearch = debounce((value: string) => {
    setProducts([])
    setPageProducts(1)
    setProductNameSearch(value)
  }, 500)

  const onChangeProductsPriceStatus = (status: EcommerceProductStatusEnum) => {
    setCurrentPageProductsPrice(1)
    setProductPriceStatus(status)
  }

  const onChangeCurrentPageProductsPrice = (page: number) => {
    setCurrentPageProductsPrice(page)
  }

  const onChangeLimitPageProductsPrice = (limit: number) => {
    setCurrentPageProductsPrice(1)
    setLimitPageProductsPrice(limit)
  }

  const onChangeSortStockPrice = (field: AdminsEcommerceProductPricelistListSortEnum) => {
    if (field === sortProductsPrice) {
      setDirectionProductsPrice(
        directionProductsPrice === OrderDirectionEnum.Asc
          ? OrderDirectionEnum.Desc
          : OrderDirectionEnum.Asc,
      )
    } else {
      setSortProductsPrice(field)
      setDirectionProductsPrice(OrderDirectionEnum.Asc)
    }
  }

  const onFetchProductPrice = async (id: string) => {
    try {
      setLoadingFetchProductPrice(true)
      setFailedFetchProductPrice(false)
      setStockLocationId(id)
      const { data } = await adminsApi.adminsEcommerceProductPricelistList(
        '',
        id,
        productPriceStatus,
        sortProductsPrice,
        directionProductsPrice,
        currentPageProductsPrice,
        limitPageProductsPrice,
      )
      setProductsPrice(data.items)
      setLoadingFetchProductPrice(false)
      setTotalRowsProductsPrice(data.pagination.total)
      setInitialFetchProductPrice(false)
    } catch {
      setFailedFetchProductPrice(true)
      setLoadingFetchProductPrice(false)
    }
  }

  const fetchProducts = async () => {
    try {
      setFailedProducts(false)
      setLoadingProducts(true)
      const { data } = await adminsApi.adminsEcommerceGetListProduct(
        '',
        productStatus,
        productNameSearch,
        'name',
        undefined,
        pageProducts,
        limitPageProducts,
      )
      setFailedProducts(false)
      setLoadingProducts(false)
      setProducts(products.concat(data.items))
      setTotalPagesProducts(data.pagination.pages)
      setInitFetchProducts(false)
    } catch (err) {
      setFailedProducts(true)
      setLoadingProducts(false)
    }
  }

  const onFetchDetail = async () => {
    try {
      const updatedStockId = searchParams.get('stockId') as string
      const updatedPriceListId = searchParams.get('priceListId') as string

      setIsLoadingForm(true)
      const [stock, priceList] = await Promise.all([
        supplychainApi.adminsSupplyChainInventoryStockGetDetail(updatedStockId),
        adminsApi.adminsEcommerceProductPricelistGetDetail('', updatedPriceListId),
      ])

      const { stockAmount, productId } = stock.data
      const {
        productName,
        priceStartTimeUtc0,
        priceEndTimeUtc0,
        status,
        isInquiryAllowed,
        isShownWorker,
        isShownMember,
        isShownAmbassador,
        isShownFarmer,
        preparationDuration,
        prices,
      } = priceList.data

      const updatedPrices: FormPrice[] = prices.map(priceItem => ({
        id: priceItem.id,
        minimumQuantity: priceItem.minimumQuantity,
        maximumQuantity: priceItem.maximumQuantity ? priceItem.maximumQuantity : 0,
        price: priceItem.price,
      }))

      setFormStockPrice({
        endDateUtc0: priceEndTimeUtc0,
        isActive: status === EcommerceProductStatusEnum.Active,
        isInquiryAllowed: isInquiryAllowed,
        isShowAmbassador: isShownAmbassador,
        isShowFarmer: isShownFarmer,
        isShowMember: isShownMember,
        isShowWorker: isShownWorker,
        minimumOrder: updatedPrices[0].minimumQuantity,
        preparationDuration: preparationDuration,
        price: updatedPrices[0].price,
        prices: updatedPrices,
        productId: productId,
        productName: productName,
        startDateUtc0: priceStartTimeUtc0,
        stockAmount: stockAmount,
        storeId: params.id as string,
      })
      setStockId(updatedStockId)
      setPriceListId(updatedPriceListId)
      setIsLoadingForm(false)
    } catch {
      navigate(`/inventory/stock-location/stock/${params.id}`, {
        state: {
          message: 'Data tidak ditemukan',
          actionType: 'danger',
        },
        replace: true,
      })
    }
  }

  const onSubmitForm = async (value: FormProductPriceValue) => {
    if (stockId === '' && priceListId === '') {
      await onCreateProductStockPriceList(value)
    } else if (stockId !== '' && priceListId !== '') {
      await onUpdateProductStockPriceList(value)
    }
  }

  const createPricesPayload = (prices: FormPrice[]) => {
    return prices.map((price, index) => {
      if (index === prices.length - 1) {
        return { minimumQuantity: price.minimumQuantity, price: price.price }
      }
      return {
        minimumQuantity: price.minimumQuantity,
        price: price.price,
        maximumQuantity: price.maximumQuantity,
      }
    })
  }

  const onCreateProductStockPriceList = async (value: FormProductPriceValue) => {
    try {
      setIsLoadingForm(true)
      const productId = value.productId
      const storeId = value.storeId
      const isActive = value.isActive
      const priceListsCreate = ecommerceApi.adminsEcommerceProductPriceListsCreate({
        productId: productId,
        storeId: storeId,
        startDateUtc0: value.startDateUtc0,
        endDateUtc0: value.endDateUtc0,
        isActive: isActive,
        isShowMember: value.isShowMember,
        isShowWorker: value.isShowWorker,
        isShowAmbassador: value.isShowAmbassador,
        isShowFarmer: value.isShowFarmer,
        minimumOrder: value.prices[0].minimumQuantity,
        preparationDuration: value.preparationDuration,
        price: value.prices[0].price,
        prices: createPricesPayload(value.prices),
        isInquiryAllowed: value.isInquiryAllowed,
      })
      const stockCreate = supplychainApi.adminsSupplyChainInventoryStockCreate({
        productId: productId,
        storeId: storeId,
        stockAmount: value.stockAmount,
        isActive: isActive,
      })
      await Promise.all([priceListsCreate, stockCreate])
      setIsLoadingForm(false)
      navigate(`/inventory/stock-location/stock/${value.storeId}`, {
        state: {
          message: 'Stok & Harga Berhasil ditambahkan',
          actionType: 'success',
        },
        replace: true,
      })
    } catch (err) {
      await toast('Stok & Harga Gagal ditambahkan', 'danger')
      setIsLoadingForm(false)
    }
  }

  const onUpdateProductStockPriceList = async (value: FormProductPriceValue) => {
    try {
      setIsLoadingForm(true)
      const payloadPriceUpdate: AdminsEcommerceProductPriceListsUpdateV2Request = {
        endDateUtc0: value.endDateUtc0,
        preparationDuration: value.preparationDuration,
        previousEndDateUtc0: formStockPrice.endDateUtc0,
        previousStartDateUtc0: formStockPrice.startDateUtc0,
        prices: createPricesPayload(value.prices),
        productId: value.productId,
        startDateUtc0: value.startDateUtc0,
        storeId: value.storeId,
        isActive: value.isActive,
        isInquiryAllowed: value.isInquiryAllowed,
        isShowMember: value.isShowMember,
        isShowWorker: value.isShowWorker,
        isShowAmbassador: value.isShowAmbassador,
        isShowFarmer: value.isShowFarmer,
      }
      const payloadStockUpdate: AdminsSupplyChainInventoryStockUpdateRequest = {
        productId: value.productId,
        stockAmount: value.stockAmount,
        storeId: value.storeId,
        isActive: value.isActive,
      }

      const priceUpdate = ecommerceApi.adminsEcommerceProductPriceListsUpdateV2(payloadPriceUpdate)
      const stockUpdate = supplychainApi.adminsSupplyChainInventoryStockUpdate(
        stockId,
        payloadStockUpdate,
      )
      await Promise.all([priceUpdate, stockUpdate])
      navigate(`/inventory/stock-location/stock/${value.storeId}`, {
        state: {
          message: 'Stok & Harga diperbarui',
          actionType: 'success',
        },
        replace: true,
      })
      setIsLoadingForm(false)
    } catch (e) {
      await toast('Stok & Harga Gagal diupdate', 'danger')
      setIsLoadingForm(false)
    }
  }

  return {
    toast,
    toastContainer,
    isLoadingFetchProductPrice,
    isFailedFetchProductPrice,
    isLoadingProducts,
    isFailedProducts,
    isLoadingForm,
    productsPrice,
    limitPageProductsPrice,
    currentPageProductsPrice,
    totalRowsProductsPrice,
    sortProductsPrice,
    directionProductsPrice,
    productPriceStatus,
    formStockPrice,
    products,
    onChangeProductsPriceStatus,
    onChangeCurrentPageProductsPrice,
    onChangeLimitPageProductsPrice,
    onFetchProductPrice,
    onChangeSortStockPrice,
    onChangePageProducts,
    onDropDownScrollFetch,
    onChangeProductNameSearch,
    fetchProducts,
    onSubmitForm,
  }
}
